import { ajvResolver } from '@hookform/resolvers/ajv'
import { Form } from 'components/form'
import { useAlert } from 'contexts/alert'
import { ICommercialPlanDTO } from 'data/dtos/commercial/i-commercial-plan-dto'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import CommerciaPlanSchema, { ICommerciaPlanFormData } from 'schema/comercial/commercial-plan'
import api from 'services/api'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}
const CommercialPlanForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [readonly, setReadonly] = useState(false)
  const { closeAlert, openAlert } = useAlert()
  const params = useParams<IRouteParams>()
  const firstInputElement = useRef(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setFocus,
  } = useForm<ICommerciaPlanFormData>({
    resolver: ajvResolver(CommerciaPlanSchema),
    defaultValues: {
      name: '',
      fixedPrice: 0,
      lifePrice: 0,
      lifeLimit: 0,
      lifeAfterLimitPrice: 0,
      recipePrice: 0,
      recipeLimit: 0,
      recipeAfterLimitPrice: 0,
      recipeExecutionPrice: 0,
      recipeExecutionLimit: 0,
      recipeAfterExecutionLimitPrice: 0,
      connectorPrice: 0,
      connectorLimit: 0,
      connectorAfterLimitPrice: 0,
      connectorExecutionPrice: 0,
      connectorExecutionLimit: 0,
      connectorAfterExecutionLimitPrice: 0,
      isInactive: false,
    },
  })

  const handleChange = (formField: any) => closeAlert()

  // main data load
  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data
      await api
        .get(`/commercial-plans/${id}`)
        .then((response) => {
          const { data } = response.data

          const commercialPlanResult = {
            name: data.name,
            fixedPrice: data.fixedPrice,
            lifePrice: data.lifePrice,
            lifeLimit: data.lifeLimit,
            lifeAfterLimitPrice: data.lifeAfterLimitPrice,
            recipePrice: data.recipePrice,
            recipeLimit: data.recipeLimit,
            recipeAfterLimitPrice: data.recipeAfterLimitPrice,
            recipeExecutionPrice: data.recipeExecutionPrice,
            recipeExecutionLimit: data.recipeExecutionLimit,
            recipeAfterExecutionLimitPrice: data.recipeAfterExecutionLimitPrice,
            connectorPrice: data.connectorPrice,
            connectorLimit: data.connectorLimit,
            connectorAfterLimitPrice: data.connectorAfterLimitPrice,
            connectorExecutionPrice: data.connectorExecutionPrice,
            connectorExecutionLimit: data.connectorExecutionLimit,
            connectorAfterExecutionLimitPrice: data.connectorAfterExecutionLimitPrice,
            isInactive: data.isInactive,
          }

          return commercialPlanResult
        })
        .then((commercialPlanResult: ICommercialPlanDTO) => {
          reset(commercialPlanResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
      setReadonly(true)
    }
  }, [params, params.id])

  // data save
  const onSubmit = useCallback(async (data: ICommercialPlanDTO) => {
    setIsLoading(true)

    const payLoad: ICommercialPlanDTO = {
      name: data.name,
      fixedPrice: data.fixedPrice,
      lifePrice: data.lifePrice,
      lifeLimit: data.lifeLimit,
      lifeAfterLimitPrice: data.lifeAfterLimitPrice,
      recipePrice: data.recipePrice,
      recipeLimit: data.recipeLimit,
      recipeAfterLimitPrice: data.recipeAfterLimitPrice,
      recipeExecutionPrice: data.recipeExecutionPrice,
      recipeExecutionLimit: data.recipeExecutionLimit,
      recipeAfterExecutionLimitPrice: data.recipeAfterExecutionLimitPrice,
      connectorPrice: data.connectorPrice,
      connectorLimit: data.connectorLimit,
      connectorAfterLimitPrice: data.connectorAfterLimitPrice,
      connectorExecutionPrice: data.connectorExecutionPrice,
      connectorExecutionLimit: data.connectorExecutionLimit,
      connectorAfterExecutionLimitPrice: data.connectorAfterExecutionLimitPrice,
      isInactive: data.isInactive,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/commercial-plans`, payLoad)
        .then(() => {
          openAlert('Cliente atualizado com sucesso!', 'success')

          history.push('/commercial-plans')
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    } else {
      await api
        .post('/commercial-plans', payLoad)
        .then(() => {
          openAlert('Dado cadastrado com sucesso!', 'success')
          reset()
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  useEffect(() => {
    // setFocus(firstInputElement)
  }, [setFocus])

  return (
    <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
      <Form.Info
        title='Planos Comerciais'
        subTitle='Dados coletados sobre planos comerciais.'
      />
      <Form.Body>
        <Form.Input
          label='Nome do Plano'
          error={!!errors.name}
          errorMessage={errors?.name?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={100}
          name='name'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Preço Fixo'
          error={!!errors.fixedPrice}
          errorMessage={errors?.fixedPrice?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
          maxLength={10}
          name='fixedPrice'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Preço por Vida'
          error={!!errors.lifePrice}
          errorMessage={errors?.lifePrice?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
          maxLength={10}
          name='lifePrice'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Limite de Vidas'
          error={!!errors.lifeLimit}
          errorMessage={errors?.lifeLimit?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
          maxLength={10}
          name='lifeLimit'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Preço por Vida Excedente'
          error={!!errors.lifeAfterLimitPrice}
          errorMessage={errors?.lifeAfterLimitPrice?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
          maxLength={10}
          name='lifeAfterLimitPrice'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Preço por Receita'
          error={!!errors.recipePrice}
          errorMessage={errors?.recipePrice?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='recipePrice'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Limite de Receitas'
          error={!!errors.recipeLimit}
          errorMessage={errors?.recipeLimit?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='recipeLimit'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Preço por Receita Excedente'
          error={!!errors.recipeAfterLimitPrice}
          errorMessage={errors?.recipeAfterLimitPrice?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='recipeAfterLimitPrice'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Preço por Execução de Receita'
          error={!!errors.recipeExecutionPrice}
          errorMessage={errors?.recipeExecutionPrice?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='recipeExecutionPrice'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Limite de Execuções de Receitas'
          error={!!errors.recipeExecutionLimit}
          errorMessage={errors?.recipeExecutionLimit?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='recipeExecutionLimit'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Preço por Execução de Receita Excedente'
          error={!!errors.recipeAfterExecutionLimitPrice}
          errorMessage={errors?.recipeAfterExecutionLimitPrice?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='recipeAfterExecutionLimitPrice'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Preço por Conector'
          error={!!errors.connectorPrice}
          errorMessage={errors?.connectorPrice?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='connectorPrice'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Limite de Conectores'
          error={!!errors.connectorLimit}
          errorMessage={errors?.connectorLimit?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='connectorLimit'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Preço por Conector Excedente'
          error={!!errors.connectorAfterLimitPrice}
          errorMessage={errors?.connectorAfterLimitPrice?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='connectorAfterLimitPrice'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Preço por Execução de Conector'
          error={!!errors.connectorExecutionPrice}
          errorMessage={errors?.connectorExecutionPrice?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='connectorExecutionPrice'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Limite de Execuções de Conector'
          error={!!errors.connectorExecutionLimit}
          errorMessage={errors?.connectorExecutionLimit?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='connectorExecutionLimit'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Preço por Execução de Conector Excedente'
          error={!!errors.connectorAfterExecutionLimitPrice}
          errorMessage={errors?.connectorAfterExecutionLimitPrice?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='connectorAfterExecutionLimitPrice'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.CheckBox
          label='Inativo'
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          name='isInactive'
          control={control}
        />
      </Form.Body>

      <Form.Divisor />

      <Form.Footer>
        <Form.Button
          label='SALVAR'
          type='submit'
          variant='contained'
          isLoading={isLoading}
        />
        <Form.Button
          label='CANCELAR'
          type='cancel'
          variant='text'
          isLoading={isLoading}
        />
      </Form.Footer>
    </Form.Root>
  )
}

export default CommercialPlanForm
